import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { greeklish } from "@/lib/format";
import { categorySlug } from "@/lib/category-icon";
import { buildAttributeFacets, matchesAttrs, type AttributeFacet } from "@/lib/product-attributes";
import { complementaryCategoryIds } from "@/lib/cross-sell-map";
import { DISCONTINUED_PS_IDS_PG_LIST } from "@/lib/discontinued-products";
import {
  NEW_PRODUCTS_LIMIT,
  compareNewestFirst,
  compareOldestFirst,
  pickNewestByPsId,
} from "@/lib/new-products";
import { parseSize, sizeMatchScore } from "@/lib/size-match";
import { getProductPresence, filterCategoriesWithProducts } from "@/lib/products-presence.server";
import {
  applyEffectivePricing,
  kaiText,
  normalizeKaiRow,
  getActiveOfferProductIds,
  getAllActivePromoMap,
  type ProductRow,
} from "@/lib/products.server";


const baseSelect = "health_score, id, ps_id, name, reference, ean13, price, tax_rate, bottle_deposit, stock_quantity, active, image_url, category_id, category_name, manufacturer_id, manufacturer_name, units_sold, date_add, created_at, updated_at, description_short, description, availability";

// Lighter projection for product-card grids (home rows, listings, offers,
// best-sellers). Excludes the heavy full `description` HTML to keep the
// SSR payload small and fast — cards only ever use `description_short`.
const cardSelect = "health_score, id, ps_id, name, reference, price, tax_rate, bottle_deposit, stock_quantity, active, image_url, category_id, category_name, manufacturer_id, manufacturer_name, units_sold, date_add, created_at, updated_at, description_short, availability";

// Φέρνει ενεργά/διαθέσιμα προϊόντα με βάση λίστα uuid, σε παρτίδες ώστε να μην
// ξεπερνιέται το όριο μήκους URL του PostgREST όταν τα ids είναι πολλά.
async function fetchActiveProductsByIds(ids: string[]): Promise<ProductRow[]> {
  const out: ProductRow[] = [];
  const chunkSize = 200;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const { data } = await supabaseAdmin.from("products").select(cardSelect)
      .eq("active", true).gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .in("id", ids.slice(i, i + chunkSize)) as {
      data: ProductRow[] | null;
    };
    if (data) out.push(...data);
  }
  return out;
}

export type ListingFacets = {
  manufacturers: { id: number; name: string; count: number }[];
  categories: { id: number; name: string; count: number }[];
  availability: { value: string; count: number }[];
  priceMin: number;
  priceMax: number;
  attributes: AttributeFacet[];
};

/**
 * Επιστρέφει τα `ps_id` των προϊόντων που έχουν ΕΝΕΡΓΗ προσφορά η οποία
 * ισχύει τώρα (παράθυρο ημερομηνιών) ΚΑΙ δίνει πραγματική έκπτωση
 * (τελική τιμή < κανονική). Χρησιμοποιεί την ίδια λογική τιμολόγησης με
 * το `applyEffectivePricing`, ώστε το «σε προσφορά» να σημαίνει το ίδιο
 * πράγμα παντού.
 */
async function getDiscountedPsIds(): Promise<number[]> {
  const offerIds = await getActiveOfferProductIds();
  if (offerIds.length === 0) return [];
  const rows = await fetchActiveProductsByIds(offerIds);
  const priced = await applyEffectivePricing(rows);
  return priced
    .filter((p) => p.original_price != null && p.ps_id != null)
    .map((p) => p.ps_id as number);
}

export const listProductsPaged = createServerFn({ method: "GET" })
  .inputValidator((input: {
    mode?: "all" | "category" | "new" | "offers" | "best" | "search";
    categoryPsId?: number;
    subCategoryPsIds?: number[];
    manufacturerIds?: number[];
    lockedManufacturerId?: number;
    onlyOffers?: boolean;
    availability?: string[];
    priceMin?: number;
    priceMax?: number;
    sort?: "new" | "old" | "price_asc" | "price_desc" | "alpha_asc" | "alpha_desc" | "best";
    q?: string;
    healthMin?: number;
    ingredientMin?: number;
    ingredientTags?: string[];
    page?: number;
    pageSize?: number;
  }) =>
    z.object({
      mode: z.enum(["all", "category", "new", "offers", "best", "search"]).default("all"),
      categoryPsId: z.number().int().optional(),
      subCategoryPsIds: z.array(z.number().int()).max(2000).optional(),
      manufacturerIds: z.array(z.number().int()).max(200).optional(),
      lockedManufacturerId: z.number().int().optional(),
      onlyOffers: z.boolean().optional(),
      availability: z.array(z.string().max(80)).max(50).optional(),
      priceMin: z.number().min(0).optional(),
      priceMax: z.number().min(0).optional(),
      sort: z.enum(["new", "old", "price_asc", "price_desc", "alpha_asc", "alpha_desc", "best"]).default("new"),
      q: z.string().max(200).optional(),
      healthMin: z.number().int().min(0).max(100).optional(),
      ingredientMin: z.number().int().min(0).max(100).optional(),
      ingredientTags: z.array(z.string().max(40)).max(10).optional(),
      page: z.number().int().min(1).default(1),
      pageSize: z.number().int().min(1).max(200).default(80),
      attrs: z.record(z.string().max(20), z.array(z.string().max(40)).max(50)).optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const useSql = data.mode === "all" || data.mode === "category";
    const empty = { products: [] as ProductRow[], total: 0, facets: { manufacturers: [], categories: [], availability: [], priceMin: 0, priceMax: 0, attributes: [] } as ListingFacets, error: null as string | null };
    const hasAttrFilter = !!data.attrs && Object.values(data.attrs).some((v) => v.length);

    // Σύνολο ps_ids προϊόντων που είναι ΟΝΤΩΣ σε προσφορά τώρα (έγκυρο
    // παράθυρο ημερομηνιών + πραγματική έκπτωση), όταν ζητείται "μόνο προσφορές".
    let offerPsIds: number[] | null = null;
    if (data.onlyOffers) {
      offerPsIds = await getDiscountedPsIds();
      if (offerPsIds.length === 0) return empty;
    }

    // Φίλτρα συστατικών (καλλυντικά/καθαριστικά): ids από τον πίνακα ανάλυσης.
    let ingIds: string[] | null = null;
    if (data.ingredientMin != null || data.ingredientTags?.length) {
      const { ingredientFilterIds } = await import("./ingredients.server");
      ingIds = await ingredientFilterIds(data.ingredientMin, data.ingredientTags);
      if (!ingIds || ingIds.length === 0) return empty;
    }

    if (useSql) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const applyScope = (q: any, forFacets = false): any => {
        let out = q.eq("active", true).gt("stock_quantity", 0)
          .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST);
        if (data.lockedManufacturerId != null) out = out.eq("manufacturer_id", data.lockedManufacturerId);
        if (data.mode === "category" && data.categoryPsId != null) {
          const ids = data.subCategoryPsIds?.length ? data.subCategoryPsIds : [data.categoryPsId];
          out = out.in("category_id", ids);
        } else if (data.subCategoryPsIds?.length && !forFacets) {
          out = out.in("category_id", data.subCategoryPsIds);
        }
        return out;
      };

      // Facet scan (περιλαμβάνει name για τα έξυπνα φίλτρα χαρακτηριστικών).
      // Για τα facets ΔΕΝ εφαρμόζουμε το φίλτρο κατηγορίας/μάρκας που επέλεξε ο
      // χρήστης, ώστε η λίστα επιλογών να παραμένει σταθερή (να μη συρρικνώνεται).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let facetQ: any = applyScope(supabaseAdmin.from("products").select("manufacturer_id, manufacturer_name, category_id, category_name, availability, price, name"), true);
      if (offerPsIds) facetQ = facetQ.in("ps_id", offerPsIds);
      if (data.priceMin != null) facetQ = facetQ.gte("price", data.priceMin);
      if (data.priceMax != null) facetQ = facetQ.lte("price", data.priceMax);
      const { data: facetRows } = await facetQ.limit(5000) as {
        data: { manufacturer_id: number | null; manufacturer_name: string | null; category_id: number | null; category_name: string | null; availability: string | null; price: number | null; name: string | null }[] | null;
      };
      const facets = buildFacets(facetRows ?? []);

      const orderBy = (q: any) => {
        if (data.sort === "price_asc") return q.order("price", { ascending: true });
        if (data.sort === "price_desc") return q.order("price", { ascending: false });
        if (data.sort === "alpha_asc") return q.order("name", { ascending: true });
        if (data.sort === "alpha_desc") return q.order("name", { ascending: false });
        if (data.sort === "best") return q.order("units_sold", { ascending: false });
        if (data.sort === "old") return q.order("ps_id", { ascending: false, nullsFirst: false });
        return q.order("ps_id", { ascending: true, nullsFirst: false });
      };

      if (hasAttrFilter) {
        // Φόρτωση όλων των προϊόντων του scope και φιλτράρισμα/σελιδοποίηση in-memory,
        // ώστε να εφαρμοστούν τα φίλτρα χαρακτηριστικών (που προκύπτουν από το όνομα).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let allQ: any = applyScope(supabaseAdmin.from("products").select(cardSelect));
        if (data.manufacturerIds?.length) allQ = allQ.in("manufacturer_id", data.manufacturerIds);
        if (offerPsIds) allQ = allQ.in("ps_id", offerPsIds);
        if (data.availability?.length) allQ = allQ.in("availability", data.availability);
        if (data.healthMin != null) allQ = allQ.gte("health_score", data.healthMin);
        if (ingIds) allQ = allQ.in("id", ingIds);
        if (data.priceMin != null) allQ = allQ.gte("price", data.priceMin);
        if (data.priceMax != null) allQ = allQ.lte("price", data.priceMax);
        allQ = orderBy(allQ);
        const { data: allRows, error: allErr } = await allQ.limit(6000) as {
          data: ProductRow[] | null; error: { message: string } | null;
        };
        if (allErr) return { ...empty, error: allErr.message };
        const matched = (allRows ?? []).filter((r) => matchesAttrs(r.name, data.attrs));
        const total = matched.length;
        const from = (data.page - 1) * data.pageSize;
        const products = await applyEffectivePricing(matched.slice(from, from + data.pageSize));
        return { products, total, facets, error: null };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query: any = applyScope(supabaseAdmin.from("products").select(baseSelect, { count: "exact" }));
      if (data.manufacturerIds?.length) query = query.in("manufacturer_id", data.manufacturerIds);
      if (offerPsIds) query = query.in("ps_id", offerPsIds);
      if (data.availability?.length) query = query.in("availability", data.availability);
      if (data.healthMin != null) query = query.gte("health_score", data.healthMin);
      if (ingIds) query = query.in("id", ingIds);
      if (data.priceMin != null) query = query.gte("price", data.priceMin);
      if (data.priceMax != null) query = query.lte("price", data.priceMax);
      query = orderBy(query);
      const from = (data.page - 1) * data.pageSize;
      const { data: rows, count, error } = await query.range(from, from + data.pageSize - 1) as {
        data: ProductRow[] | null; count: number | null; error: { message: string } | null;
      };
      if (error) return { ...empty, error: error.message };
      const products = await applyEffectivePricing((rows ?? []) as ProductRow[]);
      return { products, total: count ?? 0, facets, error: null };
    }

    // In-memory modes
    let base: ProductRow[] = [];
    if (data.mode === "search") {
      const q = data.q?.trim();
      if (q) {
        const like = `%${q.replace(/[%_]/g, (m) => `\\${m}`)}%`;
        const { data: rows, error } = await supabaseAdmin
          .from("products").select(cardSelect)
          .eq("active", true).gt("stock_quantity", 0)
          .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
          .or(`name.ilike.${like},reference.ilike.${like}`)
          .limit(1000);
        if (error) return { ...empty, error: error.message };
        base = (rows ?? []) as ProductRow[];
      }
    } else if (data.mode === "offers") {
      // Μόνο προϊόντα με τρέχουσα, πραγματική έκπτωση (ίδια λογική παντού).
      const psIds = await getDiscountedPsIds();
      if (psIds.length === 0) base = [];
      else {
        const { data: rows, error } = await supabaseAdmin.from("products").select(cardSelect)
          .eq("active", true).gt("stock_quantity", 0)
          .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
          .in("ps_id", psIds).limit(5000);
        if (error) return { ...empty, error: error.message };
        base = (rows ?? []) as ProductRow[];
      }
    } else if (data.mode === "new") {
      // Νέες αφίξεις: τα 100 προϊόντα με το μεγαλύτερο ps_id. Το ps_id είναι ο
      // αξιόπιστος δείκτης νεότητας — τα πιο καινούργια προϊόντα έχουν
      // `date_add = null`, οπότε καμία ταξινόμηση κατά ημερομηνία δεν τα βγάζει.
      const { data: rows, error } = await supabaseAdmin.from("products").select(cardSelect)
        .eq("active", true).gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .order("ps_id", { ascending: false, nullsFirst: false })
        .limit(NEW_PRODUCTS_LIMIT);
      if (error) return { ...empty, error: error.message };
      base = pickNewestByPsId((rows ?? []) as ProductRow[], NEW_PRODUCTS_LIMIT);
    } else if (data.mode === "best") {
      const { data: rows } = await supabaseAdmin.from("products").select(cardSelect)
        .eq("active", true).gt("stock_quantity", 0).gt("units_sold", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .order("units_sold", { ascending: false }).limit(2000);
      base = (rows ?? []) as ProductRow[];
    }

    let filtered = base;
    if (offerPsIds) {
      const set = new Set(offerPsIds);
      filtered = filtered.filter((p) => p.ps_id != null && set.has(p.ps_id));
    }
    if (data.subCategoryPsIds?.length) {
      const set = new Set(data.subCategoryPsIds);
      filtered = filtered.filter((p) => p.category_id != null && set.has(p.category_id));
    }
    if (ingIds) {
      const set = new Set(ingIds);
      filtered = filtered.filter((p) => set.has(p.id));
    }
    if (data.healthMin != null) filtered = filtered.filter((p) => p.health_score != null && Number(p.health_score) >= data.healthMin!);
    if (data.priceMin != null) filtered = filtered.filter((p) => p.price != null && Number(p.price) >= data.priceMin!);
    if (data.priceMax != null) filtered = filtered.filter((p) => p.price != null && Number(p.price) <= data.priceMax!);

    const facets = buildFacets(filtered);

    if (data.manufacturerIds?.length) {
      const set = new Set(data.manufacturerIds);
      filtered = filtered.filter((p) => p.manufacturer_id != null && set.has(p.manufacturer_id));
    }

    if (data.availability?.length) {
      const set = new Set(data.availability);
      filtered = filtered.filter((p) => p.availability != null && set.has(p.availability));
    }

    if (hasAttrFilter) {
      filtered = filtered.filter((p) => matchesAttrs(p.name, data.attrs));
    }

    // «Νεότερα/Παλαιότερα» = ps_id (τα πιο καινούργια προϊόντα έχουν date_add = null).
    if (data.sort === "price_asc") filtered.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
    else if (data.sort === "price_desc") filtered.sort((a, b) => Number(b.price ?? 0) - Number(a.price ?? 0));
    else if (data.sort === "alpha_asc") filtered.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "el"));
    else if (data.sort === "alpha_desc") filtered.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? "", "el"));
    else if (data.sort === "best") filtered.sort((a, b) => Number((b.units_sold ?? 1)) - Number((a.units_sold ?? 1)));
    else if (data.sort === "old") filtered.sort(compareNewestFirst);
    else filtered.sort(compareOldestFirst);

    const total = filtered.length;
    const from = (data.page - 1) * data.pageSize;
    const products = await applyEffectivePricing(filtered.slice(from, from + data.pageSize));
    return { products, total, facets, error: null };
  });

function buildFacets(rows: { manufacturer_id: number | null; manufacturer_name: string | null; category_id?: number | null; category_name?: string | null; availability?: string | null; price: number | null; name?: string | null }[]): ListingFacets {
  const mfMap = new Map<number, { id: number; name: string; count: number }>();
  const catMap = new Map<number, { id: number; name: string; count: number }>();
  const availMap = new Map<string, { value: string; count: number }>();
  let pMin = Infinity, pMax = 0;
  for (const r of rows) {
    if (r.manufacturer_id != null) {
      const e = mfMap.get(r.manufacturer_id);
      if (e) e.count++;
      else mfMap.set(r.manufacturer_id, { id: r.manufacturer_id, name: kaiText(r.manufacturer_name) ?? "—", count: 1 });
    }
    if (r.category_id != null) {
      const e = catMap.get(r.category_id);
      if (e) e.count++;
      else catMap.set(r.category_id, { id: r.category_id, name: kaiText(r.category_name) ?? "—", count: 1 });
    }
    const av = r.availability?.trim();
    if (av) {
      const e = availMap.get(av);
      if (e) e.count++;
      else availMap.set(av, { value: av, count: 1 });
    }
    if (r.price != null) {
      const v = Number(r.price);
      if (Number.isFinite(v)) {
        if (v < pMin) pMin = v;
        if (v > pMax) pMax = v;
      }
    }
  }
  return {
    manufacturers: Array.from(mfMap.values()).sort((a, b) => b.count - a.count),
    categories: Array.from(catMap.values()).sort((a, b) => b.count - a.count),
    availability: Array.from(availMap.values()).sort((a, b) => b.count - a.count),
    priceMin: pMin === Infinity ? 0 : Math.floor(pMin),
    priceMax: Math.ceil(pMax),
    attributes: buildAttributeFacets(rows.map((r) => r.name)),
  };
}

export const listProducts = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number; categoryId?: number; q?: string; sort?: "new" | "old" | "price_asc" | "price_desc" | "alpha_asc" | "alpha_desc" | "best" }) =>
    z.object({
      limit: z.number().int().min(1).max(100).optional(),
      categoryId: z.number().int().optional(),
      q: z.string().max(200).optional(),
      sort: z.enum(["new", "old", "price_asc", "price_desc", "alpha_asc", "alpha_desc", "best"]).optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const q = data.q?.trim();
    if (q) {
      const like = `%${q.replace(/[%_]/g, (m) => `\\${m}`)}%`;
      const { data: rows, error } = await supabaseAdmin
        .from("products").select(cardSelect)
        .eq("active", true).gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .or(`name.ilike.${like},reference.ilike.${like}`)
        .limit(data.limit ?? 24);
      if (error) return { products: [], error: error.message };
      type ProductRow = {
        id: string; ps_id: number | null; name: string | null; reference: string | null;
        price: number | null; tax_rate: number | null; bottle_deposit: number | null;
        stock_quantity: number | null; active: boolean;
        image_url: string | null; category_id: number | null; category_name: string | null;
        manufacturer_id: number | null; manufacturer_name: string | null;
        units_sold: number | null; date_add: string | null; created_at: string; updated_at: string;
        original_price?: number | null; offer_ends_at?: string | null;
      };
      let products = ((rows as ProductRow[] | null) ?? []);
      if (data.categoryId) products = products.filter((p) => p.category_id === data.categoryId);
      if (data.sort === "price_asc") products.sort((a, b) => Number(a.price) - Number(b.price));
      else if (data.sort === "price_desc") products.sort((a, b) => Number(b.price) - Number(a.price));
      else if (data.sort === "alpha_asc") products.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "el"));
      else if (data.sort === "alpha_desc") products.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? "", "el"));
      else if (data.sort === "best") products.sort((a, b) => Number((b.units_sold ?? 1)) - Number((a.units_sold ?? 1)));
      else if (data.sort === "old") products.sort(compareNewestFirst);
      else products.sort(compareOldestFirst);
      const withPrices = await applyEffectivePricing(products);
      return { products: withPrices, error: null };
    }
    let query = supabaseAdmin
      .from("products")
      .select(cardSelect)
      .eq("active", true)
      .gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .limit(data.limit ?? 24);
    if (data.categoryId) query = query.eq("category_id", data.categoryId);
    if (data.sort === "price_asc") query = query.order("price", { ascending: true });
    else if (data.sort === "price_desc") query = query.order("price", { ascending: false });
    else if (data.sort === "alpha_asc") query = query.order("name", { ascending: true });
    else if (data.sort === "alpha_desc") query = query.order("name", { ascending: false });
    else if (data.sort === "best") query = query.order("units_sold", { ascending: false });
    else if (data.sort === "old") query = query.order("ps_id", { ascending: false, nullsFirst: false });
    else query = query.order("ps_id", { ascending: true, nullsFirst: false });
    const { data: rows, error } = await query;
    if (error) return { products: [], error: error.message };
    const products = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    return { products, error: null };
  });

export const getProductByIdPrefix = createServerFn({ method: "GET" })
  .inputValidator((input: { idPrefix: string }) =>
    z.object({ idPrefix: z.string().min(1).max(36) }).parse(input),
  )
  .handler(async ({ data }) => {
    const isNumeric = /^\d+$/.test(data.idPrefix);
    const isFullUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.idPrefix);
    let q = supabaseAdmin.from("products").select(baseSelect).eq("active", true).limit(1);
    if (isNumeric) q = q.eq("ps_id", Number(data.idPrefix));
    else if (isFullUuid) q = q.eq("id", data.idPrefix);
    else q = q.filter("id::text", "ilike", `${data.idPrefix}%`);
    const { data: rows, error } = await q;
    if (error) return { product: null, error: error.message };
    type RowT = NonNullable<typeof rows>[number];
    const withPrices = await applyEffectivePricing((rows ?? []) as unknown as ProductRow[]);
    const first = (withPrices[0] ?? null) as unknown as RowT | null;
    return { product: first, error: null };
  });

// Για παλιά PrestaShop links που δείχνουν σε ανενεργό/μη διαθέσιμο προϊόν:
// εντοπίζει το προϊόν (ΑΓΝΟΩΝΤΑΣ το `active` φίλτρο) και επιστρέφει το path
// της κατηγορίας του ώστε ο caller να κάνει 301 redirect εκεί (αντί για 404).
// Επιστρέφει `{ to: null }` αν δεν βρεθεί προϊόν ή η κατηγορία είναι ανενεργή
// (τότε ο caller κάνει fallback στην αρχική ή 404).
export const getLegacyRedirectTarget = createServerFn({ method: "GET" })
  .inputValidator((input: { idPrefix: string }) =>
    z.object({ idPrefix: z.string().min(1).max(36) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ to: string | null }> => {
    const isNumeric = /^\d+$/.test(data.idPrefix);
    const isFullUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.idPrefix);
    // ΣΗΜ: χωρίς `.eq("active", true)` ώστε να πιάνουμε και τα ανενεργά.
    let q = supabaseAdmin
      .from("products")
      .select("ps_id, category_id, category_name")
      .limit(1);
    if (isNumeric) q = q.eq("ps_id", Number(data.idPrefix));
    else if (isFullUuid) q = q.eq("id", data.idPrefix);
    else q = q.filter("id::text", "ilike", `${data.idPrefix}%`);
    const { data: rows, error } = await q;
    if (error) return { to: null };
    const prod = (rows ?? [])[0] as
      | { category_id: number | null; category_name: string | null }
      | undefined;
    if (!prod || prod.category_id == null) return { to: null };
    // Επιβεβαίωση ότι η κατηγορία είναι ενεργή· αλλιώς fallback (αρχική).
    const { data: catRows } = await supabaseAdmin
      .from("categories")
      .select("ps_id, name, active")
      .eq("ps_id", prod.category_id)
      .limit(1);
    const cat = (catRows ?? [])[0] as
      | { ps_id: number; name: string | null; active: boolean | null }
      | undefined;
    if (!cat || cat.active === false) return { to: null };
    return { to: `/c/${categorySlug(cat.ps_id, cat.name ?? prod.category_name)}` };
  });

// Επιστρέφει όλες τις φωτογραφίες ενός προϊόντος (gallery) από τον πίνακα
// `product_images`, ταξινομημένες κατά `position`. Αν δεν υπάρχουν, ο caller
// κάνει fallback στο κύριο `image_url` του προϊόντος.
export const getProductImages = createServerFn({ method: "GET" })
  .inputValidator((input: { productId: string }) =>
    z.object({ productId: z.string().min(1).max(64) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ images: { url: string; position: number }[] }> => {
    const { data: rows, error } = await supabaseAdmin
      .from("product_images")
      .select("url, position")
      .eq("product_id", data.productId)
      .order("position", { ascending: true });
    if (error) return { images: [] };
    const images = (rows ?? [])
      .filter((r) => typeof r.url === "string" && r.url.length > 0)
      .map((r) => ({ url: r.url as string, position: Number(r.position ?? 0) }));
    return { images };
  });

// Επιστρέφει «παρόμοια προϊόντα» (ίδια κατηγορία) και προϊόντα του ίδιου
// κατασκευαστή, εξαιρώντας το τρέχον προϊόν. Χρησιμοποιείται στη σελίδα
// προϊόντος για τις δύο ενότητες προτάσεων.
export const listRelatedProducts = createServerFn({ method: "GET" })
  .inputValidator((input: { excludeId: string; categoryId?: number; manufacturerId?: number; limit?: number }) =>
    z.object({
      excludeId: z.string().min(1).max(64),
      categoryId: z.number().int().optional(),
      manufacturerId: z.number().int().optional(),
      limit: z.number().int().min(1).max(48).optional(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 12;
    const fetchBy = async (column: "category_id" | "manufacturer_id", value: number) => {
      const { data: rows, error } = await supabaseAdmin
        .from("products")
        .select(cardSelect)
        .eq("active", true)
        .gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .eq(column, value)
        .neq("id", data.excludeId)
        .order("units_sold", { ascending: false })
        .limit(limit);
      if (error) return [] as ProductRow[];
      return await applyEffectivePricing((rows ?? []) as ProductRow[]);
    };
    const [similar, sameManufacturer] = await Promise.all([
      data.categoryId != null ? fetchBy("category_id", data.categoryId) : Promise.resolve([] as ProductRow[]),
      data.manufacturerId != null ? fetchBy("manufacturer_id", data.manufacturerId) : Promise.resolve([] as ProductRow[]),
    ]);
    return { similar, sameManufacturer, error: null };
  });

// «Έξυπνες» προτάσεις συμπληρωματικών προϊόντων (cross-sell) για αύξηση του
// μέσου καλαθιού. Με βάση τις κατηγορίες των προϊόντων (είτε δοσμένες απευθείας
// είτε αντλημένες από τα `fromProductIds`), βρίσκει τις συμπληρωματικές
// κατηγορίες (π.χ. Απορρυπαντικά Ρούχων → Μαλακτικά) και επιστρέφει δημοφιλή,
// διαθέσιμα προϊόντα από αυτές, εξαιρώντας ό,τι ήδη υπάρχει στο καλάθι.
export const listCrossSell = createServerFn({ method: "GET" })
  .inputValidator((input: { categoryIds?: number[]; fromProductIds?: string[]; excludeProductIds?: string[]; limit?: number }) =>
    z.object({
      categoryIds: z.array(z.number().int()).max(40).optional(),
      fromProductIds: z.array(z.string().min(1).max(64)).max(60).optional(),
      excludeProductIds: z.array(z.string().min(1).max(64)).max(120).optional(),
      limit: z.number().int().min(1).max(12).optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 6;
    const exclude = new Set(data.excludeProductIds ?? []);
    const sourceCats = new Set<number>(data.categoryIds ?? []);
    if (data.fromProductIds?.length) {
      const { data: rows } = await supabaseAdmin
        .from("products")
        .select("id, category_id")
        .in("id", data.fromProductIds) as { data: { id: string; category_id: number | null }[] | null };
      for (const r of rows ?? []) if (r.category_id != null) sourceCats.add(r.category_id);
    }
    const targets = complementaryCategoryIds(Array.from(sourceCats));
    if (targets.length === 0) return { products: [] as ProductRow[], error: null as string | null };
    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select(cardSelect)
      .eq("active", true)
      .gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .in("category_id", targets)
      .order("units_sold", { ascending: false })
      .limit(limit * 4);
    if (error) return { products: [] as ProductRow[], error: error.message };
    const priced = await applyEffectivePricing(
      ((rows ?? []) as ProductRow[]).filter((p) => !exclude.has(p.id)),
    );
    // Διατήρηση της σειράς προτεραιότητας των κατηγοριών-στόχων: πρώτα προϊόντα
    // της πιο σχετικής συμπληρωματικής κατηγορίας.
    const rank = new Map(targets.map((c, i) => [c, i]));
    priced.sort((a, b) => (rank.get(a.category_id ?? -1) ?? 99) - (rank.get(b.category_id ?? -1) ?? 99));
    return { products: priced.slice(0, limit), error: null as string | null };
  });

// Παρόμοια προϊόντα για το καλάθι: αντλεί δημοφιλή, διαθέσιμα προϊόντα από τις
// ΙΔΙΕΣ κατηγορίες με αυτά που έχει ήδη ο πελάτης στο καλάθι (σε αντίθεση με το
// cross-sell που ψάχνει συμπληρωματικές κατηγορίες). Έτσι εμφανίζονται πάντα
// σχετικές προτάσεις που μπορεί να προσθέσει άμεσα στο καλάθι.
export const listSimilarForCart = createServerFn({ method: "GET" })
  .inputValidator((input: { fromProductIds?: string[]; excludeProductIds?: string[]; limit?: number }) =>
    z.object({
      fromProductIds: z.array(z.string().min(1).max(64)).max(60).optional(),
      excludeProductIds: z.array(z.string().min(1).max(64)).max(120).optional(),
      limit: z.number().int().min(1).max(12).optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 6;
    const exclude = new Set(data.excludeProductIds ?? []);
    if (!data.fromProductIds?.length) return { products: [] as ProductRow[], error: null as string | null };
    const { data: srcRows } = await supabaseAdmin
      .from("products")
      .select("id, category_id")
      .in("id", data.fromProductIds) as { data: { id: string; category_id: number | null }[] | null };
    const cats = new Set<number>();
    for (const r of srcRows ?? []) if (r.category_id != null) cats.add(r.category_id);
    const targets = Array.from(cats);
    if (targets.length === 0) return { products: [] as ProductRow[], error: null as string | null };
    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select(cardSelect)
      .eq("active", true)
      .gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .in("category_id", targets)
      .order("units_sold", { ascending: false })
      .limit(limit * 5);
    if (error) return { products: [] as ProductRow[], error: error.message };
    const priced = await applyEffectivePricing(
      ((rows ?? []) as ProductRow[]).filter((p) => !exclude.has(p.id)),
    );
    return { products: priced.slice(0, limit), error: null as string | null };
  });


export const listCategories = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data, error } = await supabaseAdmin
      .from("categories")
      .select("id, ps_id, name, active, parent_ps_id, level_depth, position, icon_url" as string)
      .eq("active", true)
      .order("level_depth", { ascending: true })
      .order("position", { ascending: true })
      .order("name", { ascending: true }) as {
      data: Record<string, unknown>[] | null;
      error: { message: string } | null;
    };
    type CategoryRow = {
      id: string; ps_id: number; name: string; active: boolean;
      parent_ps_id: number | null; level_depth: number | null;
      position: number | null; icon_url: string | null;
    };
    if (error) return { categories: [] as CategoryRow[], error: error.message };
    const categories = (data ?? []).map((r) => normalizeKaiRow(r)) as unknown as CategoryRow[];

    // Αποκλείουμε κατηγορίες χωρίς διαθέσιμα προϊόντα (άμεσα ή στους απογόνους).
    const presence = await getProductPresence();
    if (!presence) return { categories, error: null };
    const filtered = filterCategoriesWithProducts(categories, presence.categoryPsIds);
    return { categories: filtered, error: null };
  });



export const getManufacturerByPsId = createServerFn({ method: "GET" })
  .inputValidator((input: { psId: number }) =>
    z.object({ psId: z.number().int() }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: rows, error } = await supabaseAdmin
      .from("manufacturers")
      .select("id, ps_id, name, logo_url")
      .eq("ps_id", data.psId)
      .limit(1) as {
      data: { id: string; ps_id: number; name: string; logo_url: string | null }[] | null;
      error: { message: string } | null;
    };
    if (error) return { manufacturer: null, error: error.message };
    return { manufacturer: rows?.[0] ? normalizeKaiRow(rows[0]) : null, error: null };
  });

export const listManufacturers = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data: rows, error } = await supabaseAdmin
      .from("manufacturers")
      .select("ps_id, name, logo_url")
      .eq("active", true)
      .limit(5000) as {
      data: { ps_id: number; name: string; logo_url: string | null }[] | null;
      error: { message: string } | null;
    };
    if (error) return { manufacturers: [] as { ps_id: number; name: string; logo_url: string | null }[], error: error.message };
    const presence = await getProductPresence();
    const manufacturers = (rows ?? [])
      .filter((r) => presence == null || presence.manufacturerPsIds.has(r.ps_id))
      .map((r) => normalizeKaiRow(r)) as { ps_id: number; name: string; logo_url: string | null }[];
    return { manufacturers, error: null };
  });


// Επιστρέφει ελαφρύ κατάλογο μαρκών που έχουν logo, για το σήμα μάρκας
// πάνω στις κάρτες προϊόντων (κάτω δεξιά γωνία της εικόνας).
export const listBrandLogos = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data: rows, error } = await supabaseAdmin
      .from("manufacturers")
      .select("ps_id, name, logo_url")
      .eq("active", true)
      .not("logo_url", "is", null)
      .limit(3000) as {
      data: { ps_id: number; name: string; logo_url: string | null }[] | null;
      error: { message: string } | null;
    };
    if (error) return { brands: [] as { ps_id: number; name: string; logo_url: string | null }[], error: error.message };
    const presence = await getProductPresence();
    const brands = (rows ?? [])
      .filter((r) => !!r.logo_url && !isHiddenBrand(r.name) && (presence == null || presence.manufacturerPsIds.has(r.ps_id)))
      .map((r) => normalizeKaiRow(r)) as { ps_id: number; name: string; logo_url: string | null }[];
    return { brands, error: null as string | null };
  });


export const listOffers = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number }) =>
    z.object({ limit: z.number().int().min(1).max(100).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    // Προϊόντα με ενεργή προσφορά προμηθευτή (active_supplier_offers).
    const offerIds = await getActiveOfferProductIds();
    if (offerIds.length === 0) return { products: [], error: null };
    const rows = await fetchActiveProductsByIds(offerIds);
    const priced = await applyEffectivePricing(rows);
    // Keep only products with a real, currently-active discount.
    const products = priced
      .filter((p) => p.original_price != null)
      .slice(0, data.limit ?? 60);
    return { products, error: null };
  });

export const listBestSellers = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number }) =>
    z.object({ limit: z.number().int().min(1).max(100).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 80;
    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select(cardSelect)
      .eq("active", true)
      .gt("stock_quantity", 0)
      .gt("units_sold", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .order("units_sold", { ascending: false })
      .limit(limit);
    if (error) return { products: [], error: error.message };
    const products = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    return { products, error: null };
  });

// ---------------------------------------------------------------------------
// Smart search: load ALL active, in-stock products (searchable fields only) so
// the storefront can perform fast, accent-insensitive, in-memory search/ranking.
// ---------------------------------------------------------------------------
const searchSelect =
  "id, ps_id, name, reference, ean13, upc, price, tax_rate, stock_quantity, image_url, category_id, category_name, manufacturer_id, manufacturer_name, units_sold, date_add, description_short, availability";

export type SearchProductRow = {
  id: string; ps_id: number | null; name: string | null; reference: string | null;
  ean13: string | null; upc: string | null;
  price: number | null; tax_rate: number | null; stock_quantity: number | null;
  image_url: string | null; category_id: number | null; category_name: string | null;
  manufacturer_id: number | null; manufacturer_name: string | null;
  units_sold: number | null; date_add: string | null;
  description_short: string | null; availability: string | null;
  original_price: number | null;
  offer_ends_at: string | null;
  barcodes: string[];
};

export const listAllSearchProducts = createServerFn({ method: "GET" })
  .handler(async () => {
    const pageSize = 1000;

    // 1) Load every active, in-stock product in pages of 1000.
    const all: Omit<SearchProductRow, "original_price" | "offer_ends_at" | "barcodes">[] = [];
    let from = 0;
    while (true) {
      const { data: rows, error } = await supabaseAdmin
        .from("products")
        .select(searchSelect)
        .eq("active", true)
        .gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .order("ps_id", { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) return { products: [] as SearchProductRow[], error: error.message };
      const batch = (rows ?? []) as unknown as Omit<SearchProductRow, "original_price" | "offer_ends_at" | "barcodes">[];
      all.push(...batch);
      if (batch.length < pageSize) break;
      from += pageSize;
    }

    // 2) Φόρτωση όλων των ενεργών προσφορών προμηθευτών μία φορά, με κλειδί το
    //    uuid του προϊόντος.
    const promoByProduct = await getAllActivePromoMap();

    // 2b) Load all secondary barcodes once and index them by product id.
    const barcodesByProduct = new Map<string, string[]>();
    let bFrom = 0;
    while (true) {
      const { data: bcs, error } = await supabaseAdmin
        .from("product_barcodes")
        .select("product_id, code")
        .range(bFrom, bFrom + pageSize - 1) as {
        data: { product_id: string; code: string | null }[] | null; error: { message: string } | null;
      };
      if (error) break;
      const batch = bcs ?? [];
      for (const b of batch) {
        if (!b.code) continue;
        const arr = barcodesByProduct.get(b.product_id) ?? [];
        arr.push(b.code);
        barcodesByProduct.set(b.product_id, arr);
      }
      if (batch.length < pageSize) break;
      bFrom += pageSize;
    }

    // 3) Εφαρμογή τιμολόγησης προσφοράς in-memory: τιμή προσφοράς (promo_price,
    //    με ΦΠΑ) έναντι της τρέχουσας τιμής eshop (products.price).
    const products: SearchProductRow[] = all.map((r) => {
      r = normalizeKaiRow(r);
      const eshopGross = r.price != null ? +Number(r.price).toFixed(2) : 0;
      let price = eshopGross;
      let original_price: number | null = null;
      let offer_ends_at: string | null = null;
      const pick = promoByProduct.get(r.id);
      if (pick && pick.promo > 0 && pick.promo < eshopGross) {
        price = pick.promo;
        // Διαγραμμένη («πριν») = η ΜΕΓΑΛΥΤΕΡΗ από original_price όψης και τιμή eshop.
        const offerOriginal = pick.original != null && pick.original > 0 ? pick.original : eshopGross;
        original_price = Math.max(offerOriginal, eshopGross);
        offer_ends_at = pick.endsAt;
      }
      return { ...r, price, original_price, offer_ends_at, barcodes: barcodesByProduct.get(r.id) ?? [] };
    });

    return { products, error: null as string | null };
  });

// ---------------------------------------------------------------------------
// Αρχική σελίδα: προσωποποιημένες σειρές + ενότητες κατηγοριών
// ---------------------------------------------------------------------------

/** Επιστρέφει πλήρη προϊόντα από μια λίστα UUID (διατηρεί τη σειρά εισόδου). */
export const listProductsByIds = createServerFn({ method: "GET" })
  .inputValidator((input: { ids: string[] }) =>
    z.object({ ids: z.array(z.string().min(1).max(40)).max(60) }).parse(input ?? { ids: [] }),
  )
  .handler(async ({ data }) => {
    if (data.ids.length === 0) return { products: [], error: null as string | null };
    const { data: rows, error } = await supabaseAdmin
      .from("products").select(cardSelect)
      .eq("active", true).gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .in("id", data.ids);
    if (error) return { products: [], error: error.message };
    const priced = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    const byId = new Map(priced.map((p) => [p.id, p]));
    const products = data.ids.map((id) => byId.get(id)).filter((p): p is ProductRow => !!p);
    return { products, error: null };
  });

/**
 * Επιστρέφει όλες τις κατηγορίες βάθους level_depth=3 (οι "βάθους 2" όπως τις
 * αντιλαμβάνεται ο χρήστης, π.χ. Snacks, Ένδυση, Οικιακά χημικά) ταξινομημένες
 * από τις πιο δημοφιλείς (περισσότερες πωλήσεις) στις λιγότερο δημοφιλείς.
 */
export const listHomeCategorySections = createServerFn({ method: "GET" })
  .handler(async () => {
    type Cat = { ps_id: number; name: string; parent_ps_id: number | null; level_depth: number | null; position: number | null };
    const { data: cats } = await supabaseAdmin
      .from("categories")
      .select("ps_id, name, parent_ps_id, level_depth, position")
      .eq("active", true)
      .limit(5000) as { data: Cat[] | null };
    const list = cats ?? [];
    const byPs = new Map(list.map((c) => [c.ps_id, c]));
    const ancestor3 = (psId: number): number | null => {
      let node: Cat | undefined = byPs.get(psId);
      let guard = 0;
      while (node && (node.level_depth ?? 0) > 3 && guard++ < 25) {
        node = node.parent_ps_id != null ? byPs.get(node.parent_ps_id) : undefined;
      }
      return node && node.level_depth === 3 ? node.ps_id : null;
    };
    const units = new Map<number, number>();
    const counts = new Map<number, number>();
    let from = 0;
    const page = 1000;
    for (;;) {
      const { data: rows } = await supabaseAdmin
        .from("products").select("category_id, units_sold")
        .eq("active", true).gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .range(from, from + page - 1) as { data: { category_id: number | null; units_sold: number | null }[] | null };

      if (!rows || rows.length === 0) break;
      for (const r of rows) {
        if (r.category_id == null) continue;
        const anc = ancestor3(r.category_id);
        if (anc == null) continue;
        units.set(anc, (units.get(anc) ?? 0) + Number(r.units_sold ?? 0));
        counts.set(anc, (counts.get(anc) ?? 0) + 1);
      }
      if (rows.length < page) break;
      from += page;
      if (from > 40000) break;
    }
    const sections = list
      .filter((c) => c.level_depth === 3 && (counts.get(c.ps_id) ?? 0) > 0)
      .map((c) => ({
        ps_id: c.ps_id,
        name: kaiText(c.name),
        popularity: units.get(c.ps_id) ?? 0,
        count: counts.get(c.ps_id) ?? 0,
      }))
      .sort((a, b) => b.count - a.count || b.popularity - a.popularity || a.name.localeCompare(b.name, "el"));
    return { sections, error: null as string | null };
  });

/** Κορυφαία προϊόντα (κατά πωλήσεις) μέσα σε ένα σύνολο κατηγοριών. */
export const listCategoryTopProducts = createServerFn({ method: "GET" })
  .inputValidator((input: { categoryPsIds: number[]; limit?: number }) =>
    z.object({
      categoryPsIds: z.array(z.number().int()).min(1).max(2000),
      limit: z.number().int().min(1).max(48).optional(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: rows, error } = await supabaseAdmin
      .from("products").select(cardSelect)
      .eq("active", true).gt("stock_quantity", 0)
      .in("category_id", data.categoryPsIds)
      .order("units_sold", { ascending: false })
      .limit(data.limit ?? 12);
    if (error) return { products: [], error: error.message };
    const products = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    return { products, error: null as string | null };
  });

export const listRandomBrands = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number; minProducts?: number }) =>
    z.object({
      limit: z.number().int().min(1).max(50).optional(),
      minProducts: z.number().int().min(1).max(1000).optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 12;
    const minProducts = data.minProducts ?? 10;

    // 1. Load manufacturer_ids from active, in-stock products (lightweight).
    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select("manufacturer_id")
      .eq("active", true)
      .gt("stock_quantity", 0)
      .limit(25000);

    if (error) return { brands: [] as { ps_id: number; name: string; logo_url: string | null; productCount: number; slug: string }[], error: error.message };

    // 2. Count products per manufacturer.
    const counts = new Map<number, number>();
    for (const r of rows ?? []) {
      if (r.manufacturer_id == null) continue;
      counts.set(r.manufacturer_id, (counts.get(r.manufacturer_id) ?? 0) + 1);
    }

    // 3. Filter > minProducts.
    const eligibleIds = Array.from(counts.entries())
      .filter(([, count]) => count >= minProducts)
      .map(([id]) => id);

    if (eligibleIds.length === 0) {
      return { brands: [] as { ps_id: number; name: string; logo_url: string | null; productCount: number; slug: string }[], error: null as string | null };
    }

    // 4. Shuffle (Fisher-Yates).
    for (let i = eligibleIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [eligibleIds[i], eligibleIds[j]] = [eligibleIds[j], eligibleIds[i]];
    }
    const selectedIds = eligibleIds.slice(0, limit);

    // 5. Load manufacturer details.
    const { data: mfRows } = await supabaseAdmin
      .from("manufacturers")
      .select("ps_id, name, logo_url")
      .in("ps_id", selectedIds)
      .eq("active", true) as {
      data: { ps_id: number; name: string; logo_url: string | null }[] | null;
    };

    const byPsId = new Map((mfRows ?? []).map((m) => [m.ps_id, m]));
    const brands = selectedIds
      .map((id) => {
        const m = byPsId.get(id);
        if (!m) return null;
        if (isHiddenBrand(m.name)) return null;
        return {
          ps_id: m.ps_id,
          name: kaiText(m.name),
          logo_url: m.logo_url,
          productCount: counts.get(id) ?? 0,
          slug: `${m.ps_id}-${greeklish(m.name)}`,
        };
      })
      .filter((b): b is NonNullable<typeof b> => b != null);

    return { brands, error: null as string | null };
  });

// Επικυρώνει τα είδη του καλαθιού πριν το checkout: ελέγχει αν κάθε προϊόν
// είναι ενεργό και αν το απόθεμά μας καλύπτει την επιλεγμένη ποσότητα.
export const validateCartStock = createServerFn({ method: "POST" })
  .inputValidator((input: { items: { productId: string; quantity: number }[] }) =>
    z
      .object({
        items: z
          .array(
            z.object({
              productId: z.string().min(1).max(200),
              quantity: z.number().int().min(1).max(999),
            }),
          )
          .min(1)
          .max(300),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const ids = Array.from(new Set(data.items.map((i) => i.productId)));
    const { data: rows } = await supabaseAdmin
      .from("products")
      .select("id, name, active, stock_quantity")
      .in("id", ids) as {
      data: { id: string; name: string | null; active: boolean; stock_quantity: number | null }[] | null;
    };
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));

    const issues: {
      productId: string;
      name: string;
      reason: "missing" | "inactive" | "insufficient_stock";
      available: number;
      requested: number;
    }[] = [];

    for (const item of data.items) {
      const row = byId.get(item.productId);
      const name = kaiText(row?.name) ?? "Προϊόν";
      if (!row) {
        issues.push({ productId: item.productId, name, reason: "missing", available: 0, requested: item.quantity });
        continue;
      }
      const stock = row.stock_quantity ?? 0;
      if (!row.active) {
        issues.push({ productId: item.productId, name, reason: "inactive", available: stock, requested: item.quantity });
      } else if (stock < item.quantity) {
        issues.push({ productId: item.productId, name, reason: "insufficient_stock", available: stock, requested: item.quantity });
      }
    }

    return { ok: issues.length === 0, issues };
  });

// Προτάσεις αντικατάστασης για προϊόντα που δεν είναι πλέον διαθέσιμα:
// ΠΑΝΤΑ ίδιας μάρκας και, όπου γίνεται, ίδιας περιεκτικότητας (ml/lt/gr/kg)
// και ίδιας κατηγορίας.
export const listReplacementsForUnavailable = createServerFn({ method: "POST" })
  .inputValidator((input: { productIds: string[]; limit?: number }) =>
    z
      .object({
        productIds: z.array(z.string().min(1).max(64)).min(1).max(20),
        limit: z.number().int().min(1).max(8).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 4;
    const { data: srcRows } = (await supabaseAdmin
      .from("products")
      .select("id, name, category_id, manufacturer_id, manufacturer_name")
      .in("id", data.productIds)) as {
      data:
        | { id: string; name: string | null; category_id: number | null; manufacturer_id: number | null; manufacturer_name: string | null }[]
        | null;
    };
    const sources = srcRows ?? [];
    const brandIds = Array.from(
      new Set(sources.map((s) => s.manufacturer_id).filter((v): v is number => v != null)),
    );
    let pool: ProductRow[] = [];
    if (brandIds.length > 0) {
      const { data: rows } = await supabaseAdmin
        .from("products")
        .select(cardSelect)
        .eq("active", true)
        .gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .in("manufacturer_id", brandIds)
        .order("units_sold", { ascending: false })
        .limit(600);
      pool = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    }
    const excluded = new Set(data.productIds);

    const groups = sources.map((src) => {
      const srcSize = parseSize(kaiText(src.name));
      const candidates = pool
        .filter((p) => p.manufacturer_id === src.manufacturer_id && !excluded.has(p.id))
        .map((p) => {
          const score =
            sizeMatchScore(srcSize, parseSize(p.name)) * 100 +
            (p.category_id != null && p.category_id === src.category_id ? 40 : 0) +
            Math.min(20, (p.units_sold ?? 0) / 50);
          return { p, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((c) => c.p);
      return {
        productId: src.id,
        name: kaiText(src.name) ?? "Προϊόν",
        brand: kaiText(src.manufacturer_name) ?? null,
        size: srcSize?.label ?? null,
        replacements: candidates,
      };
    });

    return { groups };
  });


/**
 * Οι 3 (ή όσες ζητηθούν) μάρκες με τα περισσότερα ενεργά προϊόντα μέσα σε ένα
 * υποδέντρο κατηγοριών — χρησιμοποιείται στις ενότητες κατηγοριών της αρχικής.
 */
// Μάρκες που δεν προβάλλονται πουθενά στις λίστες μαρκών (π.χ. η δική μας).
function isHiddenBrand(name: string | null | undefined): boolean {
  const n = (name ?? "").toLowerCase().replace(/[\s._-]/g, "");
  return n.includes("mrpanda");
}

export const listCategoryTopBrands = createServerFn({ method: "GET" })
  .inputValidator((input: { categoryPsIds: number[]; limit?: number }) =>
    z.object({
      categoryPsIds: z.array(z.number().int()).min(1).max(2000),
      limit: z.number().int().min(1).max(10).optional(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const limit = data.limit ?? 3;
    const empty = { brands: [] as { ps_id: number; name: string; logo_url: string | null; productCount: number; slug: string }[], error: null as string | null };

    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select("manufacturer_id")
      .eq("active", true)
      .gt("stock_quantity", 0)
      .in("category_id", data.categoryPsIds)
      .limit(5000) as { data: { manufacturer_id: number | null }[] | null; error: { message: string } | null };

    if (error) return { ...empty, error: error.message };

    const counts = new Map<number, number>();
    for (const r of rows ?? []) {
      if (r.manufacturer_id == null) continue;
      counts.set(r.manufacturer_id, (counts.get(r.manufacturer_id) ?? 0) + 1);
    }
    const topIds = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit + 5)
      .map(([id]) => id);
    if (topIds.length === 0) return empty;

    const { data: mfRows } = await supabaseAdmin
      .from("manufacturers")
      .select("ps_id, name, logo_url")
      .in("ps_id", topIds)
      .eq("active", true) as { data: { ps_id: number; name: string; logo_url: string | null }[] | null };

    const byPsId = new Map((mfRows ?? []).map((m) => [m.ps_id, m]));
    const brands = topIds
      .map((id) => {
        const m = byPsId.get(id);
        if (!m) return null;
        if (isHiddenBrand(m.name)) return null;
        return {
          ps_id: m.ps_id,
          name: kaiText(m.name),
          logo_url: m.logo_url,
          productCount: counts.get(id) ?? 0,
          slug: `${m.ps_id}-${greeklish(m.name)}`,
        };
      })
      .filter((b): b is NonNullable<typeof b> => b != null)
      .slice(0, limit);

    return { brands, error: null as string | null };
  });

/** Πλήθος διαθέσιμων προϊόντων ανά κατηγορία (για τη σελίδα Back to School). */
export const listCategoryProductCounts = createServerFn({ method: "GET" })
  .inputValidator((input: { categoryPsIds: number[] }) =>
    z.object({ categoryPsIds: z.array(z.number().int()).min(1).max(500) }).parse(input),
  )
  .handler(async ({ data }) => {
    const wanted = new Set(data.categoryPsIds);
    const counts = new Map<number, number>();
    let from = 0;
    const page = 1000;
    for (;;) {
      const { data: rows } = await supabaseAdmin
        .from("products")
        .select("category_id")
        .eq("active", true)
        .gt("stock_quantity", 0)
        .in("category_id", data.categoryPsIds)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .range(from, from + page - 1) as { data: { category_id: number | null }[] | null };
      if (!rows || rows.length === 0) break;
      for (const r of rows) {
        if (r.category_id == null || !wanted.has(r.category_id)) continue;
        counts.set(r.category_id, (counts.get(r.category_id) ?? 0) + 1);
      }
      if (rows.length < page) break;
      from += page;
      if (from > 40000) break;
    }
    return {
      counts: Array.from(counts.entries()).map(([ps_id, count]) => ({ ps_id, count })),
      error: null as string | null,
    };
  });

// ── Μη διαθέσιμα προϊόντα (ανενεργά ή διαγραμμένα) ─────────────────────────
// Επιστρέφει την κατάσταση ενός link προϊόντος που δεν αντιστοιχεί σε ενεργό
// προϊόν, μαζί με προτάσεις (ίδια κατηγορία / ίδια μάρκα / δημοφιλή) ώστε η
// σελίδα να δείχνει κάτι χρήσιμο αντί για σκέτο 404.
export const getUnavailableProductInfo = createServerFn({ method: "GET" })
  .inputValidator((input: { idPrefix: string }) =>
    z.object({ idPrefix: z.string().min(1).max(36) }).parse(input),
  )
  .handler(async ({ data }) => {
    const isNumeric = /^\d+$/.test(data.idPrefix);
    const isFullUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.idPrefix);
    let q = supabaseAdmin
      .from("products")
      .select("id, ps_id, name, image_url, category_id, category_name, manufacturer_id, manufacturer_name")
      .limit(1);
    if (isNumeric) q = q.eq("ps_id", Number(data.idPrefix));
    else if (isFullUuid) q = q.eq("id", data.idPrefix);
    else q = q.filter("id::text", "ilike", `${data.idPrefix}%`);
    const { data: rows } = await q;
    const prod = ((rows ?? [])[0] ?? null) as null | {
      id: string;
      ps_id: number | null;
      name: string | null;
      image_url: string | null;
      category_id: number | null;
      category_name: string | null;
      manufacturer_id: number | null;
      manufacturer_name: string | null;
    };

    const pick = async (column: "category_id" | "manufacturer_id", value: number, limit: number) => {
      const { data: r } = await supabaseAdmin
        .from("products")
        .select(cardSelect)
        .eq("active", true)
        .gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .eq(column, value)
        .neq("id", prod?.id ?? "")
        .order("units_sold", { ascending: false })
        .limit(limit);
      return await applyEffectivePricing((r ?? []) as ProductRow[]);
    };

    const popular = async (limit: number) => {
      const { data: r } = await supabaseAdmin
        .from("products")
        .select(cardSelect)
        .eq("active", true)
        .gt("stock_quantity", 0)
        .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
        .order("units_sold", { ascending: false })
        .limit(limit);
      return await applyEffectivePricing((r ?? []) as ProductRow[]);
    };

    const [sameCategory, sameManufacturer] = await Promise.all([
      prod?.category_id != null ? pick("category_id", prod.category_id, 10) : Promise.resolve([] as ProductRow[]),
      prod?.manufacturer_id != null ? pick("manufacturer_id", prod.manufacturer_id, 10) : Promise.resolve([] as ProductRow[]),
    ]);
    const related = sameCategory.length > 0 || sameManufacturer.length > 0 ? [] : await popular(10);

    let categoryPath: string | null = null;
    if (prod?.category_id != null) {
      const { data: catRows } = await supabaseAdmin
        .from("categories")
        .select("ps_id, name, active")
        .eq("ps_id", prod.category_id)
        .limit(1);
      const cat = (catRows ?? [])[0] as { ps_id: number; name: string | null; active: boolean | null } | undefined;
      if (cat && cat.active !== false) categoryPath = `/c/${categorySlug(cat.ps_id, cat.name ?? prod.category_name)}`;
    }

    return {
      status: prod ? ("inactive" as const) : ("deleted" as const),
      product: prod ? { ...prod, name: kaiText(prod.name ?? "") } : null,
      categoryPath,
      sameCategory,
      sameManufacturer,
      popular: related,
    };
  });

// Όταν η αναζήτηση δεν φέρνει αποτέλεσμα, ψάχνουμε και στα ΜΗ διαθέσιμα
// (ανενεργά ή χωρίς απόθεμα) προϊόντα με barcode/κωδικό/όνομα, ώστε ο πελάτης
// να δει τουλάχιστον ότι το προϊόν υπάρχει αλλά είναι ανενεργό.
export const lookupUnavailableByQuery = createServerFn({ method: "GET" })
  .inputValidator((input: { q: string }) =>
    z.object({ q: z.string().min(2).max(80) }).parse(input),
  )
  .handler(async ({ data }) => {
    const q = data.q.trim().replace(/[%,()]/g, "");
    if (!q) return { matches: [] as { ps_id: number | null; id: string; name: string; image_url: string | null }[] };
    const like = `%${q}%`;
    const isNumeric = /^\d+$/.test(q);
    const or = isNumeric
      ? `ean13.eq.${q},upc.eq.${q},reference.eq.${q},ps_id.eq.${Number(q) <= 2147483647 ? q : 0}`
      : `name.ilike.${like},reference.ilike.${like}`;
    const { data: rows } = await supabaseAdmin
      .from("products")
      .select("id, ps_id, name, image_url")
      .or(or)
      .limit(5);
    return {
      matches: (rows ?? []).map((r) => ({
        id: r.id as string,
        ps_id: (r.ps_id as number | null) ?? null,
        name: kaiText((r.name as string | null) ?? ""),
        image_url: (r.image_url as string | null) ?? null,
      })),
    };
  });

// Επιστρέφει τις ΕΠΙΣΗΜΕΣ (server-side) τιμές για τα προϊόντα του καλαθιού.
// Το καλάθι αποθηκεύεται τοπικά, οπότε οι τιμές του μπορεί να είναι παλιές·
// χωρίς συγχρονισμό ο πελάτης έβλεπε άλλο ποσό στο ταμείο και άλλο στη Viva
// (η παραγγελία τιμολογείται πάντα με τις τιμές του server).
export const getCartPrices = createServerFn({ method: "POST" })
  .inputValidator((input: { productIds: string[] }) =>
    z.object({ productIds: z.array(z.string().min(1).max(200)).min(1).max(300) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { getAuthoritativePricing } = await import("./products.server");
    const pricing = await getAuthoritativePricing(data.productIds);
    return {
      prices: Array.from(pricing.entries()).map(([productId, info]) => ({
        productId,
        price: info.price,
        taxRate: info.taxRate,
      })),
    };
  });

/** Προϊόντα των οποίων το όνομα περιέχει μία από τις λέξεις (προαιρετικά σε κατηγορίες). */
export const listProductsByKeywords = createServerFn({ method: "GET" })
  .inputValidator((input: { keywords: string[]; categoryPsIds?: number[]; limit?: number }) =>
    z.object({
      keywords: z.array(z.string().min(2).max(40).regex(/^[^,()*%"\\]+$/)).min(1).max(40),
      categoryPsIds: z.array(z.number().int()).max(200).optional(),
      limit: z.number().int().min(1).max(48).optional(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const orExpr = data.keywords.map((k) => `name.ilike.*${k}*`).join(",");
    let q: any = supabaseAdmin
      .from("products").select(cardSelect)
      .eq("active", true).gt("stock_quantity", 0)
      .not("ps_id", "in", DISCONTINUED_PS_IDS_PG_LIST)
      .or(orExpr);
    if (data.categoryPsIds?.length) q = q.in("category_id", data.categoryPsIds);
    const { data: rows, error } = await q.order("units_sold", { ascending: false }).limit(data.limit ?? 24);
    if (error) return { products: [], error: error.message as string | null };
    const products = await applyEffectivePricing((rows ?? []) as ProductRow[]);
    return { products, error: null as string | null };
  });
